import { setUserIconColor, showModal, StatBubble } from 'components/AppShell';
import { Icon } from 'components/Icon';
import { signOut } from 'components/Login';
import { appendChild, type JSXChild, render } from 'engine/index';
import { Check, Hourglass, Sparkles, Trophy, X } from 'lucide-static';
import { reflectoraPlayerColors, type ReflectoraClientPlayer, type ReflectoraUpdate } from 'shared/modelTypes/reflectora';
import { debounce, lateinit, unreachable } from 'shared/util';
import { Game } from './game';
import { rpc, State } from 'engine/rpc';
import { Timer } from 'components/Timer';
import { BottomRight } from 'components/BottomRight';
import { marked } from 'marked';
import { PlayerSelector } from './team-kleuren-spel';
import { Button } from 'components/Button';
import { translate } from 'shared/translate';

export class Reflectora extends Game<'reflectora-spel'> {
	override maxPlayers: number = 5;

	scoreEl: HTMLElement = lateinit;

	answers: (string | null | undefined)[] = [];
	monster: HTMLElement = lateinit;
	amulet: HTMLElement = lateinit;
	player: ReflectoraClientPlayer = lateinit;
	view: HTMLElement = lateinit;

	override getGameName(): string {
		return __`reflectora.gameTitle`;
	}

	override defineBottomUI(): JSXChild {
		return (
			<>
				<StatBubble color="green" label={__`reflectora.coins`} value="0" id="score" />
				<StatBubble
					onClick={() =>
						showModal(
							<NotesPopup
								notes={this.player?.themeNotes ?? ''}
								onChange={(v) => {
									// console.log(v);
									rpc.sendReflectoraMessage.query({
										sessionId: State.gameId.toString(),
										update: { kind: 'save-notes', playerId: State.playerId, notes: v },
									});
								}}
							/>,
						)
					}
					color="yellow"
					label={__`Notes`}
					value=" "
					id="good-card-count"
				/>
			</>
		);
	}
	override onShellCreated(view: HTMLElement): void {
		super.onShellCreated(view);
		this.scoreEl = view.querySelector('#score')!;
		this.view = view;

		this.monster = (
			<div
				id="monster"
				class="pointer-events-none fixed bottom-3 right-3 size-32 rounded-full border-4 border-yellow-600 bg-yellow-50 bg-[url(/reflectora/zeemonster.svg)] bg-contain bg-center bg-no-repeat"
				style="display:none"
			></div>
		);
		view.append(this.monster);

		this.amulet = (
			<div
				id="amulet"
				class="pointer-events-none fixed bottom-3 right-3 size-32 rounded-full border-4 border-yellow-600 bg-yellow-50 bg-[url(/reflectora/amulet.svg)] bg-contain bg-center bg-no-repeat"
				style="display:none"
			></div>
		);
		view.append(this.amulet);
	}

	override handleUpdate(update: ReflectoraUpdate): void {
		const sessionId = State.gameId.toString();
		const playerId = State.playerId;
		const kind = update.kind;
		// const playerId = State.playerId;

		// console.log('message', update);

		switch (kind) {
			case 'trigger-effect':
				this.triggerEffect(update);
				break;
			case 'show-login-screen':
				this.showLoginScreen();
				break;
			case 'show-register-screen':
				this.showRegisterScreen('player', reflectoraPlayerColors);
				break;
			case 'update-players-state':
				this.updatePlayersState(update);
				break;
			case 'update-player-state':
				State.name = update.player.name;
				State.color = update.player.color;
				State.score = update.player.score + (update.player.reflectionScore || 0);

				this.answers = update.player.answers;
				this.monster.style.display = update.player.hasMonster ? '' : 'none';
				this.amulet.style.display = update.player.hasAmulet ? '' : 'none';
				this.player = update.player;

				this.shellNameEl.innerText = update.player.name;
				setUserIconColor(this.userIconEl, update.player.color);
				this.scoreEl.innerText = (State.score ?? 0).toString();
				break;
			case 'show-questions-screen':
				{
					render(
						this.shellContentEl,
						<QuestionsScreen
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
							// title={title}
							questions={update.questions}
							answers={this.answers}
							onAnswer={(answer, answerIndex) => {
								rpc.sendReflectoraMessage.query({
									sessionId,
									update: {
										kind: 'submit-answer',
										playerId,
										answer: answer || '',
										answerIndex,
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'move-boat-screen':
				render(
					this.shellContentEl,
					<MoveBoatScreen
						phase={update.phase}
						onSubmit={() => {
							rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'submit-movement' } });
						}}
					/>,
				);
				break;
			case 'show-chancecard-screen':
				{
					if (this.player && this.player.hasMonster) {
						// TODO: Duplicate code
						render(
							this.shellContentEl,
							<ChanceCardScreen
								card={update.card}
								action={update.action}
								message={update.message}
								options={update.options}
								onChoose={(response) =>
									rpc.sendReflectoraMessage.query({
										sessionId,
										update: { kind: 'choose-chancecard-response', playerId, response },
									})
								}
							/>,
						);
					} else {
						render(
							this.shellContentEl,
							<ChestScreen
								onFinish={() => {
									if (update.effect) {
										this.triggerEffect({ kind: 'trigger-effect', effect: update.effect });
									}
									rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'open-chest' } });
									// TODO: Duplicate code
									render(
										this.shellContentEl,
										<ChanceCardScreen
											card={update.card}
											action={update.action}
											message={update.message}
											options={update.options}
											onChoose={(response) =>
												rpc.sendReflectoraMessage.query({
													sessionId,
													update: { kind: 'choose-chancecard-response', playerId, response: translate(response) },
												})
											}
										/>,
									);
								}}
							/>,
						);
					}
				}
				break;
			case 'show-moneycard-screen':
				{
					render(
						this.shellContentEl,
						<ChestScreen
							onFinish={() => {
								if (update.effect) {
									this.triggerEffect({ kind: 'trigger-effect', effect: update.effect });
								}
								rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'open-chest' } });
								// TODO: Duplicate code
								render(
									this.shellContentEl,
									<ChanceCardScreen
										card={update.card}
										action={update.action}
										message={update.message}
										options={[{ label: __`Continue`, value: 'continue' }]}
										onChoose={(_) =>
											rpc.sendReflectoraMessage.query({
												sessionId,
												update: { kind: 'end-moneycard', playerId },
											})
										}
									/>,
								);
							}}
						/>,
					);
				}
				break;

			case 'show-wait-screen':
				{
					render(
						this.shellContentEl,
						<WaitScreen
							title={__`reflectora.wait`}
							message={
								update.playerName
									? __`reflectora.wait.forTurn ${update.playerName}`
									: update.message
										? translate(update.message)
										: ''
							}
						/>,
					);
				}
				break;
			case 'show-turnstart-screen':
				{
					render(
						this.shellContentEl,
						<ChooseDiceroll
							onChoose={(roll) => {
								rpc.sendReflectoraMessage.query({
									sessionId,
									update: {
										kind: 'submit-diceroll',
										playerId,
										roll,
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'show-choose-question-screen':
				render(
					this.shellContentEl,
					<MoveBoatScreen
						phase={0}
						onSubmit={() => {
							render(
								this.shellContentEl,
								<ChooseAnswerToPresent
									questions={update.questions}
									onChoose={(questionNumber) => {
										rpc.sendReflectoraMessage.query({
											sessionId,
											update: {
												kind: 'submit-answer-to-present',
												playerId,
												questionNumber,
											},
										});
									}}
								/>,
							);
						}}
					/>,
				);
				break;
			case 'show-present-answer-screen':
				{
					render(
						this.shellContentEl,
						<PresentAnswer
							question={update.question}
							answer={update.answer}
							number={update.number + 1}
							onFinish={() => {
								rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'finish-presenting' } });
							}}
						/>,
					);
				}
				break;

			case 'show-evaluate-question-screen':
				render(
					this.shellContentEl,
					<ChooseQuestionCourse
						timerFrom={update.timerFrom}
						timerTo={update.timerTo}
						question={update.question ? translate(update.question) : ''}
						answer={update.answer}
						onAnswer={(course) => {
							render(
								this.shellContentEl,
								<ChooseQuestionEmotions
									timerFrom={update.timerFrom}
									timerTo={update.timerTo}
									question={update.question}
									course={course}
									answer={update.answer}
									onAnswer={(emotions) => {
										render(
											this.shellContentEl,
											<WaitScreen title={__`reflectora.wait`} message={__`reflectora.wait.forEveryone`} />,
										);
										rpc.sendReflectoraMessage.query({
											sessionId,
											update: {
												kind: 'submit-question-evaluation',
												type: 'submit',
												playerId,
												koerskaart: course,
												emotions,
											},
										});
									}}
								/>,
							);
						}}
					/>,
				);
				break;
			case 'show-evaluation-results-screen':
				render(
					this.shellContentEl,
					<EvaluationResults
						question={update.question}
						answer={update.answer}
						results={update.results}
						onFinish={() => {
							rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'finish-evaluation' } });
						}}
					/>,
				);
				break;
			case 'show-conclude-question-screen':
				render(
					this.shellContentEl,
					<ChooseQuestionConclusion
						timerFrom={update.timerFrom}
						timerTo={update.timerTo}
						question={update.question}
						answer={update.answer}
						onAnswer={(conclusion) => {
							// WriteConclusionSuggestion

							render(
								this.shellContentEl,
								<WriteConclusionSuggestion
									statement={conclusion}
									onSubmit={(conclusionText) => {
										render(
											this.shellContentEl,
											<WaitScreen title={__`reflectora.wait`} message={__`reflectora.wait.forEveryone`} />,
										);
										rpc.sendReflectoraMessage.query({
											sessionId,
											update: {
												kind: 'submit-question-conclusion',
												playerId,
												conclusion,
												conclusionText,
											},
										});
									}}
								/>,
							);
						}}
					/>,
				);
				break;
			case 'show-conclusion-results-screen':
				render(
					this.shellContentEl,
					<ConclusionResults
						question={update.question ? translate(update.question) : ''}
						answer={update.answer ? translate(update.answer) : ''}
						results={update.results}
						onFinish={() => {
							rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'finish-conclusion' } });
						}}
					/>,
				);

				break;
			case 'show-write-conclusion-screen':
				render(
					this.shellContentEl,
					<WriteConclusion
						value={this.player.themeNotes}
						hasAmulet={this.player.hasAmulet}
						onAmuletUse={() => {
							rpc.sendReflectoraMessage.query({
								sessionId,
								update: {
									kind: 'use-amulet',
									playerId,
								},
							});
						}}
						onSubmit={(conclusion) => {
							rpc.sendReflectoraMessage.query({
								sessionId,
								update: {
									kind: 'submit-conclusion-text',
									playerId,
									conclusion,
								},
							});
						}}
					/>,
				);
				break;
			case 'show-conclude-wait-screen':
				render(
					this.shellContentEl,
					<WaitForConclusion
						hasAmulet={this.player.hasAmulet}
						onAmuletUse={() => {
							rpc.sendReflectoraMessage.query({
								sessionId,
								update: {
									kind: 'use-amulet',
									playerId,
								},
							});
						}}
					/>,
				);
				break;
			case 'show-conclude-rating-screen':
				render(this.shellContentEl, <ConclusionRating pointsEarned={update.rating} playerName={update.playerName} />);
				break;
			case 'show-results-screen':
				render(this.shellContentEl, <ResultsScreen results={update.players} />);
				break;
			case 'show-rate-conclusion':
				appendChild(this.view, <ShowRateConclusion theme={update.theme} rating={update.rating} />);
				break;
			default:
				unreachable(kind);
		}
	}
}
const themeNames = [__`reflectora.theme1`, __`reflectora.theme2`, __`reflectora.theme3`, __`reflectora.theme4`];

export function QuestionsScreen({
	questions,
	onAnswer,
	answers,
	timerFrom,
	timerTo,
}: {
	timerFrom: number;
	timerTo: number;
	questions: string[];
	answers: (string | null | undefined)[];
	onAnswer: (answer: string | undefined, index: number) => void;
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-balance text-center text-2xl font-bold leading-tight text-gray-900">{__`reflectora.questions.title`}</h2>
			<p class="mb-6 text-gray-700">{__`reflectora.questions.subTitle`}</p>
			<div class="flex w-full flex-col gap-5 sm:flex-row">
				{(() => {
					const afdrukColors = 'bg-blue-50 text-blue-950 border-blue-200 shadow-blue-200/50';
					const questionColors = 'bg-blue-300 text-blue-950';

					return (
						<div class={'flex-1 shrink-0 rounded-lg border p-4 shadow-md ' + afdrukColors}>
							<h3 class="mb-4 block text-center text-xl font-bold leading-none">{__`Questions`}</h3>
							<div class="flex flex-col gap-3">
								{questions.map((q, i) => {
									const questionButton: HTMLElement = (
										<button
											on:click={() =>
												showModal((confirm, cancel) => {
													let answer = answers[i];
													if (!answer) {
														answer = '';
														answers[i] = answer;
													}

													const formModal: HTMLElement = (
														<div class="rounded-lg bg-white p-6">
															<h4 class="mb-2 text-lg font-bold text-gray-900">{__`Question ${i + 1}`}</h4>
															<div
																class="max-w-[50ch] text-pretty text-gray-700"
																__innerHTML={marked(translate(q))}
															></div>

															<form
																on:submit={(e: SubmitEvent) => {
																	e.preventDefault();
																	const form = e.target as HTMLFormElement;
																	const data = new FormData(form);
																	const answerText = ((data.get('answer') ?? '') as string).trim();
																	answers[i] = answerText;
																	onAnswer(answerText, i);
																	if (answerText && answerText.length) {
																		questionButton.setAttribute('data-done', '');
																	} else {
																		questionButton.removeAttribute('data-done');
																	}
																	confirm();
																}}
															>
																<div class="py-6">
																	<label class="font-semibold text-gray-800">{__`Your answer`}</label>
																	<textarea
																		name="answer"
																		rows="5"
																		placeholder={__`Answer`}
																		class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
																	>
																		{answer || ''}
																	</textarea>
																</div>
																<div class="flex justify-between">
																	<button on:click={cancel} type="button" class="p-3 text-gray-600">
																		{__`Cancel`}
																	</button>
																	<button type="submit" class="rounded-md bg-theme-600 p-3 text-theme-50">
																		{__`Save`}
																	</button>
																</div>
															</form>
														</div>
													);
													return formModal;
												})
											}
											data-done={answers[i] ? true : undefined}
											class={'group relative block rounded-md p-3 text-center ' + questionColors}
										>
											{__`Question ${i + 1}` + `: ${themeNames[i] ? translate(themeNames[i]) : ''}`}
											<div class="absolute -right-1 -top-1 z-[1] grid size-6 place-items-center rounded-full border-b border-gray-300 bg-white opacity-0 shadow-sm group-data-[done]:opacity-100">
												<Icon icon={Check} class="size-5 text-green-600" />
											</div>
										</button>
									);

									return questionButton;
								})}
							</div>
						</div>
					);
				})()}
			</div>

			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function WaitScreen({ title, message }: { title: string; message: string }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">{title}</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{message}</pre>
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
		</div>
	);
}

export function ChooseDiceroll({ onChoose }: { onChoose: (roll: number) => void }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">{__`It's your turn`}</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{__`How many eyes did you roll?`}</pre>
			<div class="mt-8 grid w-full grid-cols-3 gap-5">
				{[1, 2, 3, 4, 5, 6].map((v) => (
					<button
						on:click={() => onChoose(v)}
						class="aspect-square size-full rounded-md border border-gray-900 text-xl text-gray-900 shadow-md"
					>
						{v}
					</button>
				))}
			</div>
		</div>
	);
}

export function ChestScreen({ onFinish }: { onFinish: () => void }) {
	const view: HTMLElement = (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<div class="text-center text-xl text-gray-800">
				<p class="font-bold">{__`reflectora.openChest`}</p>
			</div>

			<div
				class="size-full max-w-80 animate-shake bg-[url(/reflectora/schatkist-dicht.svg)] bg-contain bg-center bg-no-repeat"
				on:click={() => {
					onFinish();
				}}
			></div>
		</div>
	);

	return view;
}

export function ChanceCardScreen({
	card,
	action,
	message,
	options,
	onChoose,
}: {
	card: string;
	action: string;
	message: string;
	options: { label: string; value: string }[];
	onChoose: (res: string) => void;
}) {
	const view: HTMLElement = (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<div class="relative w-80 overflow-hidden rounded-2xl border border-yellow-600 bg-yellow-50 shadow-xl">
				<div class="flex h-28 items-center justify-center py-3 text-center font-mono text-2xl font-bold text-blue-950">
					{translate(action)}
				</div>

				<div class="relative flex justify-center py-4">
					<div class="absolute inset-0 top-1/2 bg-blue-950"></div>
					<div
						id="amulet"
						class="relative z-10 size-48 rounded-full border-4 border-yellow-600 bg-yellow-50 bg-contain bg-center bg-no-repeat"
						style={`background-image:url(/reflectora/${card}.svg)`}
					></div>
				</div>

				<div class="relative z-10 flex h-28 items-center justify-center bg-blue-950 p-4 text-center text-xl text-white">
					{translate(message)}
				</div>
			</div>

			{action === 'reflectora.seaMonsters' ? (
				<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
					<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{__`How many eyes did you roll?`}</pre>
					<div class="mt-8 grid w-full grid-cols-3 gap-5">
						{['1', '2', '3', '4', '5', '6'].map((v) => (
							<button
								on:click={() => onChoose(v)}
								class="aspect-square size-full rounded-md border border-gray-900 text-xl text-gray-900 shadow-md"
							>
								{v}
							</button>
						))}
					</div>
				</div>
			) : (
				<PlayerSelector
					players={options.map((o) => ({ name: translate(o.label), color: o.value }))}
					onSelect={(p) => {
						onChoose(p.color);
					}}
				/>
			)}
		</div>
	);
	return view;
}

export function MoveBoatScreen({ phase, onSubmit }: { phase: number; onSubmit: () => void }) {
	const placement = () => {
		switch (phase) {
			case 0:
				return __`reflectora.sailRocks`;
			case 1:
				return __`reflectora.sailBuoy`;
			case 2:
				return __`reflectora.sailIsland`;
			case 3:
				return __`reflectora.sailPort`;
		}
	};
	return (
		<div class="flex h-fit w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">{__`reflectora.moveBoat`}</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{placement()}</pre>
			<div class="mt-8 flex w-full flex-col gap-5">
				<Button
					on:click={() => {
						onSubmit();
					}}
					class="size-full rounded-md border px-2 py-3 text-xl shadow-md"
				>
					{__`Continue`}
				</Button>
			</div>
		</div>
	);
}

export function ChooseAnswerToPresent({
	questions,
	onChoose,
}: {
	questions: { question: string; index: number; answer?: string | null }[];
	onChoose: (roll: number) => void;
}) {
	const afdrukColors = 'bg-blue-300 text-blue-950 border-blue-200 shadow-blue-200/50';

	return (
		<div class="flex h-fit w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">{__`reflectora.yourTurn`}</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{__`reflectora.reflect`}</pre>
			<div class="mt-8 flex w-full flex-col gap-5">
				{questions.map(({ question, index, answer }) => {
					return (
						<button
							class={'size-full rounded-md border px-2 py-3 text-xl shadow-md ' + afdrukColors}
							on:click={() =>
								showModal((confirm, cancel) => {
									const view = (
										<div class="rounded-lg bg-white p-6">
											<h3 class="text-lg font-bold">{__`Question ${index + 1}`}</h3>
											<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">
												{translate(question)}
											</pre>
											<h3 class="text-lg font-bold">{__`reflectora.yourAnswer`}</h3>
											<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">
												{answer || __`reflectora.noAnswerEntered`}
											</pre>
											<div class="flex justify-between">
												<button on:click={cancel} type="button" class="p-3 text-gray-600">
													{__`Cancel`}
												</button>
												<button
													on:click={() => {
														confirm();
														onChoose(index);
													}}
													class="rounded-md bg-theme-600 p-3 text-theme-50"
												>
													{__`Pick this topic`}
												</button>
											</div>
										</div>
									);
									return view;
								})
							}
						>
							{index + 1}. {translate(question)}
						</button>
					);
				})}
			</div>
		</div>
	);
}

export function PresentAnswer({
	number,
	question,
	answer,
	onFinish,
}: {
	question: string;
	answer?: string | null;
	number: number;
	onFinish: () => void;
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">{__`Presentation`}</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">
				{__`reflectora.presentYourReflectionPoints`}
			</pre>

			<h3 class="text-lg font-bold">{__`Question ${number}`}</h3>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800" __innerHTML={marked(translate(question))}></pre>
			<h3 class="text-lg font-bold">{__`reflectora.yourAnswer`}</h3>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{answer || __`reflectora.noAnswerEntered`}</pre>

			<Button
				on:click={() => {
					onFinish();
				}}
			>
				{__`reflectora.presentationEnd`}
			</Button>
		</div>
	);
}

export function ChooseQuestionCourse({
	timerFrom,
	timerTo,
	question,
	answer,
	onAnswer,
}: {
	timerFrom: number;
	timerTo: number;
	question: string;
	answer?: string | null;
	onAnswer: (a: string) => void;
}) {
	return (
		<div class="flex flex-col items-center gap-4 p-6">
			<h2 class="text-center text-2xl font-bold text-gray-900">{translate(question)}</h2>
			<p>{answer || __`reflectora.noAnswerEntered`}</p>
			<div class="mt-6 flex w-full flex-col gap-6">
				{[
					{ name: __`reflectora.onCourse`, color: 'bg-green-600' },
					{ name: __`reflectora.offCourse`, color: 'bg-yellow-300' },
					{ name: __`reflectora.speedProblem`, color: 'bg-red-600' },
				].map((option) => (
					<button
						on:click={() => onAnswer(option.name)}
						class="w-full rounded-md border border-gray-900 p-4 text-center text-xl text-gray-900 shadow-md hover:bg-gray-200"
					>
						<span class={'mr-2 inline-block size-4 rounded-full bg-black ' + option.color}></span>
						{option.name}
					</button>
				))}
			</div>
			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function ChooseQuestionEmotions({
	question,
	course,
	answer,
	onAnswer,
	timerFrom,
	timerTo,
}: {
	timerFrom: number;
	timerTo: number;
	question: string;
	course: string;
	answer?: string | null;
	onAnswer: (a: string[]) => void;
}) {
	const emotionsList = [
		__`reflectora.emotion.proud`,
		__`reflectora.emotion.satisfied`,
		__`reflectora.emotion.happy`,
		__`reflectora.emotion.calm`,
		__`reflectora.emotion.confidient`,
		__`reflectora.emotion.relaxed`,
		__`reflectora.emotion.angry`,
		__`reflectora.emotion.irritated`,
		__`reflectora.emotion.disappointed`,
	];

	const selectedEmotions: string[] = [];

	const toggleEmotion = (emotion: string, course: string) => {
		const index = selectedEmotions.indexOf(emotion);
		if (index > -1) {
			selectedEmotions.splice(index, 1); // Remove emotion if it's already selected
		} else {
			selectedEmotions.push(emotion); // Add emotion if not already selected
		}
		rpc.sendReflectoraMessage.query({
			sessionId: State.gameId.toString(),
			update: {
				kind: 'submit-question-evaluation',
				type: 'update',
				playerId: State.playerId,
				koerskaart: course,
				emotions: selectedEmotions,
			},
		});
	};

	return (
		<div class="flex flex-col items-center gap-6 p-6">
			<h2 class="text-center text-2xl font-bold text-gray-900">{__`reflectora.feeling`}</h2>
			<p>{answer || __`reflectora.noAnswerEntered`}</p>
			<div class="mt-6 grid w-full grid-cols-2 gap-6 sm:grid-cols-3">
				{emotionsList.map((emotion) => {
					const btn: HTMLElement = (
						<button
							on:click={(_e: MouseEvent) => {
								btn.classList.toggle('bg-blue-500');
								btn.classList.toggle('text-white');
								btn.classList.toggle('bg-gray-200');
								btn.classList.toggle('text-gray-900');
								toggleEmotion(emotion, course);
							}}
							class="w-full rounded-md border bg-gray-200 p-4 text-center text-xl text-gray-900 shadow-md"
						>
							{emotion}
						</button>
					);
					return btn;
				})}
			</div>
			<button on:click={() => onAnswer(selectedEmotions)} class="mt-8 rounded-md bg-blue-500 px-6 py-2 text-xl text-white shadow-md">
				{__`Send`}
			</button>
			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function EvaluationResults({
	results,
	question,
	answer,
	onFinish,
}: {
	question: string;
	answer?: string | null;
	results: {
		playerName: string;
		koerskaart: string;
		emotions: string[];
	}[];
	onFinish: () => void;
}) {
	let currentIndex = 0; // Track the current user being displayed

	// Create the container where the result content will be rendered
	const view = <div class="flex flex-col items-center gap-4 p-6"></div>;

	const showResult = (index: number) => {
		const result = results[index];
		// console.log(results);
		if (!result) {
			return (
				<div>
					<p>{__`reflectora.noEvaluation`}</p>
					<Button
						on:click={() => {
							onFinish();
						}}
					>
						{__`reflectora.evaluation.end`}
					</Button>
				</div>
			);
		}
		return (
			<div class="flex flex-col items-center gap-4 p-6">
				<h2 class="text-3xl font-bold text-gray-900">{result.playerName}</h2>
				<p>
					{translate(question)} - {answer || __`reflectora.noAnswerEntered`}
				</p>
				<div class="mt-4 w-full max-w-sm rounded-md border bg-gray-100 p-4">
					<h3 class="text-xl font-semibold">{__`reflectora.routeMap`}</h3>
					<p class="text-lg text-gray-700">{result.koerskaart}</p>
				</div>
				<div class="mt-6">
					<h3 class="text-xl font-semibold">{__`reflectora.emotion`}</h3>
					<div class="mt-4 grid grid-cols-2 gap-4">
						{result.emotions.map((emotion) => (
							<span key={emotion} class="rounded-full bg-blue-500 px-4 py-2 text-lg text-white">
								{emotion}
							</span>
						))}
					</div>
				</div>
				<div class="mt-6 flex gap-4">
					<button
						disabled={currentIndex === 0} // Disable if it's the first result
						on:click={() => {
							if (currentIndex > 0) currentIndex--;
							render(view, showResult(currentIndex)); // Re-render content
						}}
						class="rounded-md bg-gray-300 px-6 py-2 text-lg text-gray-900 hover:bg-gray-400 disabled:opacity-50"
					>
						{__`Previous`}
					</button>
					<button
						disabled={currentIndex === results.length - 1} // Disable if it's the last result
						on:click={() => {
							if (currentIndex < results.length - 1) currentIndex++;
							render(view, showResult(currentIndex)); // Re-render content
						}}
						class="rounded-md bg-blue-500 px-6 py-2 text-lg text-white hover:bg-blue-600 disabled:opacity-50"
					>
						{__`Next`}
					</button>
				</div>
				{currentIndex === results.length - 1 && (
					<Button
						on:click={() => {
							onFinish();
						}}
					>
						{__`reflectora.evaluation.end`}
					</Button>
				)}
			</div>
		);
	};

	// Initially render the first result into the view container
	render(view, showResult(currentIndex));

	// Return the view container for further manipulation or mounting in the parent component
	return view;
}

export function ChooseQuestionConclusion({
	question,
	answer,
	onAnswer,
	timerFrom,
	timerTo,
}: {
	timerFrom: number;
	timerTo: number;
	question: string;
	answer?: string | null;
	onAnswer: (conclusion: string) => void;
}) {
	const conclusionOptions = [
		__`reflectora.conclusion.option1`,
		__`reflectora.conclusion.option2`,
		__`reflectora.conclusion.option3`,
		__`reflectora.conclusion.option4`,
		__`reflectora.conclusion.option5`,
		__`reflectora.conclusion.option6`,
	];

	return (
		<div class="flex flex-col items-center gap-4 p-6">
			<h2 class="text-center text-3xl font-bold text-gray-900">{translate(question)}</h2>
			<p>{answer || __`reflectora.noAnswerEntered`}</p>
			<div class="mt-6 grid w-full grid-cols-2 gap-6">
				{conclusionOptions.map((conclusion) => (
					<button
						key={conclusion}
						on:click={() => onAnswer(conclusion)}
						class="w-full rounded-md border bg-gray-200 p-6 text-xl text-gray-900 shadow-md hover:bg-blue-500 hover:text-white"
					>
						{conclusion}
					</button>
				))}
			</div>
			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function ConclusionResults({
	results,
	question,
	answer,
	onFinish,
}: {
	question: string;
	answer?: string | null;
	results: { playerName: string; conclusion: string; conclusionText: string }[];
	onFinish: () => void;
}) {
	let currentIndex = 0; // Track the current result being displayed

	// Create the container for the results
	const view = <div class="flex flex-col items-center gap-4 p-6"></div>;

	const showResult = (index: number) => {
		const result = results[index];
		if (!result) return <p>{__`reflectora.noConclusion`}.</p>;
		return (
			<div class="flex flex-col items-center gap-4 p-6">
				<h2 class="text-3xl font-bold text-gray-900">{__`reflectora.conclusion`}</h2>
				<p>
					{question} - {answer || __`reflectora.noAnswerEntered`}
				</p>
				<div class="mt-6 w-full max-w-sm rounded-md border bg-gray-100 p-4">
					<strong>{result.playerName}</strong>
					<p class="text-xl text-gray-900">{result.conclusion}</p>
					<p class="text-gray-700">{result.conclusionText}</p>
				</div>
				<div class="mt-6 flex gap-4">
					<button
						disabled={currentIndex === 0} // Disable if it's the first result
						on:click={() => {
							if (currentIndex > 0) currentIndex--;
							render(view, showResult(currentIndex)); // Re-render content
						}}
						class="rounded-md bg-gray-300 px-6 py-2 text-lg text-gray-900 hover:bg-gray-400 disabled:opacity-50"
					>
						{__`Previous`}
					</button>
					<button
						disabled={currentIndex === results.length - 1} // Disable if it's the last result
						on:click={() => {
							if (currentIndex < results.length - 1) currentIndex++;
							render(view, showResult(currentIndex)); // Re-render content
						}}
						class="rounded-md bg-blue-500 px-6 py-2 text-lg text-white hover:bg-blue-600 disabled:opacity-50"
					>
						{__`Next`}
					</button>
				</div>
				{currentIndex === results.length - 1 && (
					<Button
						on:click={() => {
							onFinish();
						}}
					>
						{__`Continue`}
					</Button>
				)}
			</div>
		);
	};

	// Initially render the first result into the view container
	render(view, showResult(currentIndex));

	// Return the view container for further manipulation or mounting in the parent component
	return view;
}

export function WriteConclusionSuggestion({ statement, onSubmit }: { statement: string; onSubmit: (conclusion: string) => void }) {
	const textarea: HTMLTextAreaElement = (
		<textarea class="h-32 w-full max-w-sm rounded-md border p-2" placeholder={__`reflectora.conclusion.write`}></textarea>
	);

	const submitButton = (
		<button
			on:click={() => {
				const conclusion = textarea.value.trim();
				if (conclusion) onSubmit(conclusion);
			}}
			class="rounded-md bg-blue-500 px-6 py-2 text-lg text-white hover:bg-blue-600"
		>
			{__`Send`}
		</button>
	);

	const view = (
		<div class="flex flex-col items-center gap-4 p-6">
			<div class="flex flex-col items-center gap-4 p-6">
				<h2 class="text-2xl font-bold text-gray-900">{statement}</h2>
				{textarea}
				{submitButton}
			</div>
		</div>
	);

	return view;
}

export function WriteConclusion({
	onSubmit,
	hasAmulet,
	onAmuletUse,
	value,
}: {
	onSubmit: (conclusion: string) => void;
	hasAmulet: boolean;
	onAmuletUse: () => void;
	value: string;
}) {
	const textarea: HTMLTextAreaElement = (
		<textarea class="h-32 w-full max-w-sm rounded-md border p-2" placeholder={__`reflectora.conclusion.write`}>
			{value}
		</textarea>
	);

	const submitButton = (
		<button
			on:click={() => {
				const conclusion = textarea.value.trim();
				if (conclusion) onSubmit(conclusion);
			}}
			class="rounded-md bg-blue-500 px-6 py-2 text-lg text-white hover:bg-blue-600"
		>
			{__`Send`}
		</button>
	);

	const view = (
		<div class="flex flex-col items-center gap-4 p-6">
			<div class="flex flex-col items-center gap-4 p-6">
				<h2 class="text-2xl font-bold text-gray-900">{__`reflectora.theme.conclusion`}</h2>
				{textarea}

				{hasAmulet && <AmuletButton onAmuletUse={onAmuletUse} />}
				{submitButton}
			</div>
		</div>
	);

	return view;
}

export function WaitForConclusion({ hasAmulet, onAmuletUse }: { hasAmulet: boolean; onAmuletUse: () => void }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">{__`Patient`}</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{__`reflectora.wait.coin`}</pre>
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
			{hasAmulet && <AmuletButton onAmuletUse={onAmuletUse} />}
		</div>
	);
}

function AmuletButton({ onAmuletUse }: { onAmuletUse: () => void }) {
	let disabled = false;

	const btn = (
		<button
			on:click={() => {
				if (disabled) return;
				btn.style.opacity = '.5';
				btn.style.pointerEvents = 'none';
				disabled = true;
				onAmuletUse();
			}}
			class="mt-4 rounded-md border border-gray-900 px-4 py-2 text-xl text-gray-900 shadow-md disabled:opacity-50"
		>
			{__`reflectora.amulet.use`}
		</button>
	);
	return btn;
}

export function ConclusionRating({ pointsEarned, playerName }: { pointsEarned: number; playerName?: string }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">
				{pointsEarned > 0 ? __`reflectora.congrats` : __`reflectora.tooBad`}
			</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">
				{playerName
					? __(['reflectora.playerCoinEarnings %'], playerName, pointsEarned)
					: __(['reflectora.youCoinEarnings %'], pointsEarned)}
			</pre>
			<Icon icon={Sparkles} class="size-20 text-gray-300" />
		</div>
	);
}

export function ResultsScreen({
	results,
}: {
	results: {
		name: string;
		color: string;
		score: number;
		scoreReflection: number;
		reflectionColors: (string | null)[];
	}[];
}) {
	return (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">{__`reflectora.finalResults`}</h2>
			<table class="overflow-hidden rounded-md shadow-sm">
				<thead>
					<tr class="bg-gray-100">
						<th class="px-5 py-1">{__`reflectora.ranking`}</th>
						<th class="px-5 py-1">{__`reflectora.pawn`}</th>
						<th class="px-5 py-1">{__`reflectora.name`}</th>
						<th class="px-5 py-1">{__`reflectora.coins`}</th>
						<th class="px-5 py-1">{__`reflectora.reflectionCoins`}</th>
						<th class="px-5 py-1">{__`reflectora.total`}</th>
					</tr>
				</thead>
				<tbody>
					{results.map((r, i) => {
						const highlight = State.color == r.color;

						const reflectionColors = r.reflectionColors
							.map((color) => (color ? `<div class="mr-1 inline-block size-4 rounded-full bg-${color}-500"></div>` : ''))
							.join('');

						return (
							<tr class={highlight ? 'bg-yellow-100 ring-2 ring-yellow-400' : 'even:bg-gray-50'}>
								<td class="px-5 py-1">
									<div class="inline-flex items-baseline gap-3">
										<span class="font-bold">{i + 1}</span>
										{i == 0 && <Icon icon={Trophy} class="size-4 text-orange-400" />}
										{i == 1 && <Icon icon={Trophy} class="size-4 text-gray-400" />}
										{i == 2 && <Icon icon={Trophy} class="size-4 text-orange-700" />}
										{i >= 3 && <Icon icon={Trophy} class="size-4 opacity-0" />}
									</div>
								</td>
								<td class="px-5 py-1">{r.color}</td>
								<td class="px-5 py-1">{r.name}</td>
								<td class="px-5 py-1">{r.score}</td>
								<td class="px-5 py-1" __innerHTML={reflectionColors}>
									{r.scoreReflection}
								</td>
								<td class="px-5 py-1">{r.score + r.scoreReflection}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={signOut}>
				{__`Quit`}
			</button>
		</div>
	);
}

export function NotesPopup({ notes, onChange }: { notes: string; onChange: (v: string) => void }) {
	const debouncedCallback = debounce(onChange, 500);
	return (
		<div class="relative flex max-h-full w-full max-w-md flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg">
			<button action-cancel class="absolute right-2 top-2 rounded-md p-2">
				<Icon icon={X} class="size-6" />
			</button>
			<h3 class="mb mb-6 text-lg font-bold text-gray-900">{__`reflectora.notes`}</h3>
			<textarea
				on:input={(e: InputEvent) => debouncedCallback((e.target as HTMLTextAreaElement).value)}
				rows="5"
				class="w-full rounded-md border border-gray-400 bg-gray-50 p-1"
			>
				{notes}
			</textarea>
		</div>
	);
}

export function ShowRateConclusion({ theme, rating }: { theme: number; rating: number }) {
	let message = __`reflectora.reflection.graded`;
	switch (rating) {
		case 100:
			message = __`reflectora.reflection.graded.100`;
			break;
		case 150:
			message = __`reflectora.reflection.graded.150`;
			break;
		case 200:
			message = __`reflectora.reflection.graded.200`;
			break;
	}

	const view = (
		<div
			rate-conclusion
			class="absolute bottom-10 left-1/2 flex w-4/5 -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg"
			// class="absolute bottom-28 left-1/2 flex w-4/5 flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg"
		>
			<button
				action-cancel
				class="absolute right-2 top-2 rounded-md p-2"
				on:click={(event: MouseEvent) => {
					const btn = event.target as HTMLElement;
					const rateConclusionPopUp = btn.closest('[rate-conclusion]');
					if (rateConclusionPopUp) {
						rateConclusionPopUp.remove();
					}
				}}
			>
				<Icon icon={X} class="size-6" />
			</button>
			<h3 class="mb mb-2 text-lg font-bold text-gray-900">{themeNames[theme] ? translate(themeNames[theme]) : ''}</h3>
			<p class="w-full p-1 text-center">{message}</p>
		</div>
	);

	return view;
}
